import { TableHead, Th, Tr, Td } from '../../Elements/DataTable';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { HiOutlineTrash, HiOutlinePlus, HiOutlineX, HiOutlineCamera, HiOutlineCheck, HiOutlinePencil, HiOutlineSearch } from "react-icons/hi";
import { UserContext } from "../../../context/UserContext";
import useConfirmar from "../../Elements/useConfirmar";
import SectionHeader from "../../Elements/SectionHeader";
import Button from "../../Elements/Button";
import { buildGastosExcelRows, gastosExcelFilename } from "./gastosExcelExport";
import './PagosTercerosPage.css';

const MONTHS = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

function formatMoney(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDateDisplay(isoString) {
  if (!isoString) return "";
  const d = new Date(isoString);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}-${p(d.getMonth() + 1)}-${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function uid() {
  return (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `d_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

// Persistencia de borradores por tienda (sobrevive a navegar/recargar).
const draftsKey = (vid) => `wa_gastos_drafts_${vid || "none"}`;
function loadDrafts(vid) {
  try {
    const arr = JSON.parse(localStorage.getItem(draftsKey(vid)) || "[]");
    if (!Array.isArray(arr)) return [];
    // Limpia estado transitorio que no debe persistir.
    return arr.map((d) => ({ ...d, saving: false, error: "" }));
  } catch { return []; }
}
function saveDrafts(vid, drafts) {
  try { localStorage.setItem(draftsKey(vid), JSON.stringify(drafts)); } catch { /* quota */ }
}

function blankLine(prefill = {}) {
  return {
    key: uid(),
    productId: prefill.productId ?? "",
    productName: prefill.productName ?? "",
    qty: prefill.qty ?? "",
    unitCost: prefill.unitCost ?? "",
    unitCostTouched: prefill.unitCostTouched ?? false,
  };
}

function blankDraft(prefill = {}) {
  const now = new Date();
  return {
    key: uid(),
    supplier: prefill.supplier ?? "",
    cif: prefill.cif ?? "",
    invoiceNumber: prefill.invoiceNumber ?? "",
    concept: prefill.concept ?? "",
    conceptGuess: prefill.conceptGuess ?? false,   // concepto sugerido por el escáner
    groupId: prefill.groupId ?? "",
    groupGuess: prefill.groupGuess ?? false,       // grupo sugerido por la IA/histórico
    groupSource: prefill.groupSource ?? null,      // "history" | "ai" | null
    amount: prefill.amount ?? "",
    amountTouched: prefill.amount != null && prefill.amount !== "",   // importe fijado (escaneado/tecleado) → no autocompletar desde líneas
    vatRate: prefill.vatRate ?? "",
    vatGuess: prefill.vatGuess ?? false,   // IVA sugerido por el escáner, no impreso
    status: prefill.status ?? "pagada",    // "pagada" | "pendiente"
    source: prefill.source ?? null,        // "ai" | "rules" | null (manual)
    date: prefill.date ?? now.toISOString().slice(0, 10),
    time: prefill.time ?? `${String(now.getHours()).padStart(2, "0")}:00`,
    saving: false,
    error: "",
    alert: null,
    dupAlert: prefill.dupAlert ?? null,    // factura ya registrada (mismo nº + proveedor)
    // Entrada de mercancía con factura (tick "añadir a almacén")
    toAlmacen: prefill.toAlmacen ?? false,
    lines: prefill.lines ?? [blankLine()],   // 1..N líneas de producto
  };
}

function cuotaOf(amount, vatRate) {
  const a = parseFloat(amount);
  const r = parseFloat(String(vatRate).replace(",", "."));
  if (!a || !r) return null;
  return a - a / (1 + r / 100);
}

// Importe del gasto sugerido desde las líneas: Σ(cantidad × coste) + IVA. "" si no hay datos.
function autoAmountFromLines(d) {
  const base = (d.lines || []).reduce((s, l) => {
    const q = parseFloat(l.qty);
    const c = parseFloat(l.unitCost);
    return s + (q > 0 && c >= 0 ? q * c : 0);
  }, 0);
  if (!base) return "";
  const r = parseFloat(String(d.vatRate).replace(",", ".")) || 0;
  return (base * (1 + r / 100)).toFixed(2);
}

// Coste unitario sugerido (solo cuando hay UNA línea, porque usa el total de la
// factura): base sin IVA ÷ cantidad de esa línea. "" si no aplica o faltan datos.
function autoUnitCost(d, line) {
  if (!d.lines || d.lines.length !== 1) return "";
  const a = parseFloat(d.amount);
  const q = parseFloat(line.qty);
  if (!a || !q) return "";
  const r = parseFloat(String(d.vatRate).replace(",", ".")) || 0;
  const base = a / (1 + r / 100);
  return (base / q).toFixed(2);
}

const inputCls = "border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring)] shadow-sm";
const cellInput = "w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--ring)]";
const fieldLabel = "block text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5";

export default function PagosTercerosPage() {
  const [token, , , venueID] = useContext(UserContext);
  const { confirmar, dialogo: dialogoConfirmar } = useConfirmar();

  const currentYear  = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const [selectedYear,   setSelectedYear]   = useState(currentYear);
  const [selectedMonth,  setSelectedMonth]  = useState(currentMonth);
  const [filterGroupId,  setFilterGroupId]  = useState("");
  const [searchQuery,    setSearchQuery]    = useState("");
  const [statusFilter,   setStatusFilter]   = useState("");   // "" | "pendiente" | "pagada"
  const [groups,         setGroups]         = useState([]);
  const [payments,       setPayments]       = useState([]);
  const [totalAmount,    setTotalAmount]    = useState(0);
  const [pendingAmount,  setPendingAmount]  = useState(0);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState("");

  // Draft rows (review-before-register)
  const [drafts,   setDrafts]   = useState(() => loadDrafts(venueID));
  const loadedVenueRef = useRef(venueID);   // tienda cuyos borradores están cargados
  const justLoadedRef  = useRef(false);     // evita guardar justo tras una recarga
  // Recargar borradores al cambiar de tienda.
  useEffect(() => {
    if (loadedVenueRef.current === venueID) return;
    loadedVenueRef.current = venueID;
    justLoadedRef.current = true;
    setDrafts(loadDrafts(venueID));
  }, [venueID]);
  // Persistir borradores de la tienda actual (sin pisar al recargar).
  useEffect(() => {
    if (loadedVenueRef.current !== venueID) return;
    if (justLoadedRef.current) { justLoadedRef.current = false; return; }
    saveDrafts(venueID, drafts);
  }, [drafts, venueID]);
  const [scanning, setScanning] = useState(false);
  const [scanMsg,  setScanMsg]  = useState("");
  const fileInputRef = useRef(null);

  // Group manager modal
  const [showGroupModal,  setShowGroupModal]  = useState(false);
  const [newGroupName,    setNewGroupName]    = useState("");
  const [groupSubmitting, setGroupSubmitting] = useState(false);
  const [groupError,      setGroupError]      = useState("");
  const [editGroupId,     setEditGroupId]     = useState(null);   // grupo en edición
  const [editGroupName,   setEditGroupName]   = useState("");

  const authHeaders = useCallback(
    () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }),
    [token]
  );

  const fetchGroups = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await fetch(`/v1/third-party-payments/groups?venue_id=${venueID}`, { headers: authHeaders() });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setGroups(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [token, venueID, authHeaders]);

  const fetchPayments = useCallback(async () => {
    if (!token || !venueID) return;
    setLoading(true); setError("");
    try {
      let url = `/v1/third-party-payments?venue_id=${venueID}&year=${selectedYear}&month=${selectedMonth + 1}`;
      if (filterGroupId) url += `&group_id=${filterGroupId}`;
      if (searchQuery.trim()) url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error("Error al cargar pagos");
      const data = await res.json();
      setPayments(data.items || []);
      setTotalAmount(data.total_amount || 0);
      setPendingAmount(data.pending_amount || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, venueID, selectedYear, selectedMonth, filterGroupId, searchQuery, statusFilter, authHeaders]);

  // Catálogo de productos (para el tick "añadir a almacén"). Búsqueda cliente.
  const [products, setProducts] = useState([]);
  const fetchProducts = useCallback(async () => {
    if (!token || !venueID) return;
    try {
      const res = await fetch(`/v1/get-venue-products?skip=0&limit=500`, {
        headers: { ...authHeaders(), "x-venue-id": venueID },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [token, venueID, authHeaders]);

  useEffect(() => { fetchGroups(); },  [fetchGroups]);
  useEffect(() => { fetchPayments(); }, [fetchPayments]);
  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // ── Draft helpers ───────────────────────────────────────────────────────────
  const patchDraft = useCallback((key, patch) => {
    setDrafts((prev) => prev.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  }, []);

  const removeDraft = useCallback((key) => {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  }, []);

  const addBlankDraft = useCallback(() => {
    setDrafts((prev) => [...prev, blankDraft()]);
  }, []);

  // ── Líneas de almacén (multi-producto por factura) ──
  const patchLine = useCallback((dKey, lineKey, patch) => {
    setDrafts((prev) => prev.map((d) => d.key === dKey
      ? { ...d, lines: d.lines.map((l) => (l.key === lineKey ? { ...l, ...patch } : l)) }
      : d));
  }, []);
  const addLine = useCallback((dKey) => {
    setDrafts((prev) => prev.map((d) => (d.key === dKey ? { ...d, lines: [...d.lines, blankLine()] } : d)));
  }, []);
  const removeLine = useCallback((dKey, lineKey) => {
    setDrafts((prev) => prev.map((d) => d.key === dKey
      ? { ...d, lines: d.lines.length > 1 ? d.lines.filter((l) => l.key !== lineKey) : d.lines }
      : d));
  }, []);

  // Autocompletar el importe del gasto desde las líneas (salvo que se haya fijado a mano/escaneo).
  useEffect(() => {
    setDrafts((prev) => {
      let changed = false;
      const next = prev.map((d) => {
        if (!d.toAlmacen || d.amountTouched) return d;
        const sugg = autoAmountFromLines(d);
        if (sugg === "" || d.amount === sugg) return d;
        changed = true;
        return { ...d, amount: sugg };
      });
      return changed ? next : prev;
    });
  }, [drafts]);

  async function handleScanInvoices(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";   // permite re-escanear los mismos archivos
    if (!files.length) return;
    setScanning(true); setScanMsg(`Escaneando ${files.length} factura(s)…`);
    let ok = 0, fail = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("group_names", JSON.stringify(groups.map((g) => g.name)));
        fd.append("venue_id", venueID);
        const res = await fetch("/v1/third-party-payments/scan", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },   // sin Content-Type: lo pone el navegador
          body: fd,
        });
        if (!res.ok) throw new Error();
        const { parsed, source } = await res.json();
        // Mapear el grupo sugerido (por nombre) a su id
        const matchedGroup = parsed?.group
          ? groups.find((g) => g.name.toLowerCase() === String(parsed.group).toLowerCase())
          : null;
        // Líneas extraídas por la IA → mini-tabla de almacén (producto sugerido + cantidad + coste).
        const aiLines = Array.isArray(parsed?.lines) ? parsed.lines : [];
        const mappedLines = aiLines.map((ln) => blankLine({
          productId: ln.product_id || "",
          productName: ln.product_name || ln.description || "",
          qty: ln.quantity != null ? String(ln.quantity) : "",
          unitCost: ln.unit_price != null ? String(ln.unit_price) : "",
          unitCostTouched: ln.unit_price != null,   // el coste viene de la factura
        }));
        const newDraft = blankDraft({
          supplier: parsed?.supplier_name || "",
          cif: parsed?.supplier_cif || "",
          invoiceNumber: parsed?.invoice_number || "",
          concept: parsed?.concept || "",
          conceptGuess: !!parsed?.concept,
          groupId: matchedGroup ? matchedGroup.id : "",
          groupGuess: !!matchedGroup,
          groupSource: parsed?.group_source || null,
          amount: parsed?.amount != null ? String(parsed.amount) : "",
          vatRate: parsed?.vat_rate != null ? String(parsed.vat_rate) : "",
          vatGuess: parsed?.vat_rate_guess === true,
          source: source || "rules",
          date: parsed?.paid_at ? String(parsed.paid_at).slice(0, 10) : undefined,
          toAlmacen: mappedLines.length > 0,
          lines: mappedLines.length ? mappedLines : undefined,
        });
        setDrafts((prev) => [...prev, newDraft]);
        if (newDraft.invoiceNumber) checkDuplicate(newDraft.key, newDraft);
        ok += 1;
      } catch { fail += 1; }
    }
    setScanning(false);
    setScanMsg(
      `${ok} factura(s) añadida(s) como borrador${fail ? `, ${fail} fallaron` : ""}. ` +
      `Revisa cada fila (sobre todo el importe) antes de registrar.`
    );
  }

  async function checkDraftPrice(key, d) {
    const a = parseFloat(d.amount);
    if (!a || (!d.supplier?.trim() && !d.concept?.trim())) { patchDraft(key, { alert: null }); return; }
    try {
      const params = new URLSearchParams({ venue_id: venueID, amount: String(a) });
      if (d.supplier?.trim()) params.set("supplier_name", d.supplier.trim());
      if (d.concept?.trim()) params.set("concept", d.concept.trim());
      const res = await fetch(`/v1/third-party-payments/price-check?${params}`, { headers: authHeaders() });
      if (!res.ok) { patchDraft(key, { alert: null }); return; }
      const data = await res.json();
      patchDraft(key, { alert: data.has_history && data.increased ? data : null });
    } catch { patchDraft(key, { alert: null }); }
  }

  async function checkDuplicate(key, d) {
    const inv = d.invoiceNumber?.trim();
    if (!inv) { patchDraft(key, { dupAlert: null }); return; }
    try {
      const params = new URLSearchParams({ venue_id: venueID, invoice_number: inv });
      if (d.supplier?.trim()) params.set("supplier_name", d.supplier.trim());
      const res = await fetch(`/v1/third-party-payments/duplicate-check?${params}`, { headers: authHeaders() });
      if (!res.ok) { patchDraft(key, { dupAlert: null }); return; }
      const data = await res.json();
      patchDraft(key, { dupAlert: data.is_duplicate ? data.existing : null });
    } catch { patchDraft(key, { dupAlert: null }); }
  }

  // Returns true on success
  async function registerOne(d) {
    if (!d.concept.trim() || !d.amount) {
      patchDraft(d.key, { error: "Falta concepto o importe." });
      return false;
    }
    if (d.toAlmacen && !(d.lines || []).some((l) => l.productId && parseFloat(l.qty) > 0)) {
      patchDraft(d.key, { error: "Para añadir a almacén, al menos una línea con producto y cantidad > 0." });
      return false;
    }
    patchDraft(d.key, { saving: true, error: "" });
    try {
      const paid_at = new Date(`${d.date}T${d.time || "00:00"}:00`).toISOString();
      const header = {
        venue_id: venueID, group_id: d.groupId || null, concept: d.concept.trim(),
        amount: parseFloat(d.amount), paid_at,
        supplier_name: d.supplier.trim() || null,
        supplier_cif: d.cif.trim() || null,
        invoice_number: d.invoiceNumber.trim() || null,
        status: d.status || "pagada",
        vat_rate: d.vatRate === "" ? null : parseFloat(String(d.vatRate).replace(",", ".")),
      };
      let res;
      if (d.toAlmacen) {
        // Entrada de mercancía con factura: crea el gasto + N entradas de stock vinculadas.
        const lines = (d.lines || [])
          .filter((l) => l.productId && parseFloat(l.qty) > 0)
          .map((l) => {
            const uc = l.unitCostTouched ? l.unitCost : autoUnitCost(d, l);
            return {
              product_id: l.productId,
              quantity: parseFloat(l.qty),
              unit_cost: uc === "" || uc == null ? null : parseFloat(uc),
            };
          });
        res = await fetch("/v1/stock/goods-receipt", {
          method: "POST", headers: { ...authHeaders(), "x-venue-id": venueID },
          body: JSON.stringify({ ...header, lines }),
        });
      } else {
        res = await fetch("/v1/third-party-payments", {
          method: "POST", headers: authHeaders(),
          body: JSON.stringify(header),
        });
      }
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Error"); }
      return true;
    } catch (err) {
      patchDraft(d.key, { saving: false, error: err.message });
      return false;
    }
  }

  async function registerDraft(key) {
    const d = drafts.find((x) => x.key === key);
    if (!d) return;
    const ok = await registerOne(d);
    if (ok) { removeDraft(key); fetchPayments(); }
  }

  async function registerAllDrafts() {
    const current = drafts;
    let any = false;
    for (const d of current) {
      const ok = await registerOne(d);
      if (ok) { removeDraft(d.key); any = true; }
    }
    if (any) { setScanMsg(""); fetchPayments(); }
  }

  async function handleCreateGroup(e) {
    e.preventDefault();
    if (!newGroupName.trim()) return;
    setGroupSubmitting(true); setGroupError("");
    try {
      const res = await fetch("/v1/third-party-payments/groups", {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ venue_id: venueID, name: newGroupName.trim() }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Error"); }
      const created = await res.json();
      setGroups((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setNewGroupName("");
    } catch (err) { setGroupError(err.message); }
    finally { setGroupSubmitting(false); }
  }

  async function handleRenameGroup(groupId) {
    const name = editGroupName.trim();
    if (!name) return;
    setGroupError("");
    try {
      const res = await fetch(`/v1/third-party-payments/groups/${groupId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ venue_id: venueID, name }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Error"); }
      const updated = await res.json();
      setGroups((prev) => prev.map((g) => (g.id === groupId ? updated : g)).sort((a, b) => a.name.localeCompare(b.name)));
      setEditGroupId(null); setEditGroupName("");
    } catch (err) { setGroupError(err.message); }
  }

  async function handleDeleteGroup(groupId) {
    // ConfirmDialog y no `window.confirm`: el nativo se traga en las tablets del
    // club y «Eliminar» moría sin decir nada (auditoría de UI, UI-003).
    const grupo = groups.find((g) => g.id === groupId);
    const eliminar = await confirmar({
      title: "Eliminar grupo",
      description: `Se eliminará ${grupo ? `«${grupo.name}»` : "el grupo"}. Los pagos asociados quedarán sin grupo.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!eliminar) return;
    setGroupError("");
    try {
      const res = await fetch(`/v1/third-party-payments/groups/${groupId}?venue_id=${venueID}`, {
        method: "DELETE", headers: authHeaders(),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Error"); }
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
      if (filterGroupId === groupId) setFilterGroupId("");
      fetchPayments();
    } catch (err) { setGroupError(err.message); }
  }

  function closeGroupModal() {
    setShowGroupModal(false); setGroupError(""); setNewGroupName("");
    setEditGroupId(null); setEditGroupName("");
  }

  async function handleDeletePayment(id) {
    const eliminar = await confirmar({
      title: "Eliminar pago",
      description: "Se eliminará este pago del listado. No se puede deshacer.",
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!eliminar) return;
    try {
      await fetch(`/v1/third-party-payments/${id}?venue_id=${venueID}`, { method: "DELETE", headers: authHeaders() });
      fetchPayments();
    } catch { /* silent */ }
  }

  // Mismo patrón que Contabilidad: el Excel es el corte ya filtrado en `payments`
  // (año/mes/grupo/estado/búsqueda; la API no pagina este listado).
  const exportFilteredPayments = useCallback(() => {
    const rows = buildGastosExcelRows(payments);
    if (rows.length === 0) {
      setError("No hay gastos filtrados para exportar.");
      return;
    }
    setError("");
    const worksheet = XLSX.utils.json_to_sheet(rows, {
      cellDates: true,
      dateNF: "dd/mm/yyyy hh:mm",
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Gastos");
    XLSX.writeFile(workbook, gastosExcelFilename(selectedYear, selectedMonth));
  }, [payments, selectedYear, selectedMonth]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const hasDrafts = drafts.length > 0;

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">

      {/* La cabecera del sistema: el hero con degradado a lima (el mismo literal
          que llevaba Estadísticas) era un segundo sistema visual (UI-031). */}
      <SectionHeader
        title="Gastos y compras"
        subtitle="Registra y consulta todos los gastos y compras por grupo y período."
        sticky={false}
      />

      {/* ── ZONA 1: Escaneo / registro ─────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-[var(--primary)]">Escanear / registrar</div>
            <p className="text-sm text-gray-500">Escanea facturas o añade filas, revísalas y regístralas.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowGroupModal(true)}
              title="Crear un grupo para clasificar pagos"
              className="flex items-center gap-1.5 px-4 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold rounded-lg transition-colors shadow-sm"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Nuevo grupo
            </button>
            <div className="hidden sm:block w-px h-7 bg-gray-200 mx-0.5" />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              className="hidden"
              onChange={handleScanInvoices}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={scanning}
              title="Escanear una o varias facturas (cámara o imágenes)"
              className="wa-gastos-scan-btn flex items-center gap-1.5 px-4 py-1.5 bg-[var(--primary)] hover:bg-[var(--primary-strong)] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm disabled:opacity-50"
            >
              <HiOutlineCamera className="w-4 h-4" />
              {scanning ? "Escaneando…" : "Escanear facturas"}
            </button>
            <button
              type="button"
              onClick={addBlankDraft}
              className="flex items-center gap-1.5 px-4 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-semibold rounded-lg transition-colors shadow-sm"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Añadir fila
            </button>
          </div>
        </div>

        {scanMsg && <p className="text-sm text-[var(--primary)] bg-[var(--muted)] px-4 py-2">{scanMsg}</p>}

        {!hasDrafts ? (
          <div className="text-center py-8 text-sm text-gray-400">
            Escanea una factura o pulsa «Añadir fila» para registrar un pago.
          </div>
        ) : (
        <>
          <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-amber-50 border-y border-amber-200">
            <div className="text-sm font-semibold text-amber-800">
              Borradores · {drafts.length} — revisa y registra
            </div>
            <button type="button" onClick={registerAllDrafts}
              className="px-4 py-1.5 bg-gray-900 hover:bg-gray-700 text-white text-xs font-semibold rounded-lg transition-colors shadow-sm">
              Registrar todos
            </button>
          </div>
          <div className="p-4 space-y-3">
            {drafts.map((d) => {
              const cuota = cuotaOf(d.amount, d.vatRate);
              return (
                <div key={d.key} className={`rounded-xl border p-3 shadow-sm ${d.error ? "border-red-200 bg-red-50/30" : "border-gray-200 bg-white"}`}>
                  <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
                    {/* Proveedor + CIF */}
                    <div className="flex-[2] min-w-[170px]">
                      <label className={fieldLabel}>Proveedor</label>
                      <input type="text" className={cellInput} value={d.supplier} placeholder="Proveedor"
                        onChange={(e) => patchDraft(d.key, { supplier: e.target.value })} />
                    </div>
                    <div className="w-[120px]">
                      <label className={fieldLabel}>CIF / NIF</label>
                      <input type="text" className={cellInput} value={d.cif} placeholder="—"
                        onChange={(e) => patchDraft(d.key, { cif: e.target.value })} />
                    </div>
                    <div className="w-[120px]">
                      <label className={fieldLabel}>Nº factura</label>
                      <input type="text"
                        className={`${cellInput} ${d.dupAlert ? "border-red-400 ring-1 ring-red-300" : ""}`}
                        value={d.invoiceNumber} placeholder="—"
                        onChange={(e) => patchDraft(d.key, { invoiceNumber: e.target.value })}
                        onBlur={() => checkDuplicate(d.key, d)} />
                      {d.dupAlert && (
                        <div className="mt-0.5 text-[11px] leading-tight text-red-600"
                          title={`Ya existe: ${d.dupAlert.supplier_name || ""} · ${formatMoney(d.dupAlert.amount)}${d.dupAlert.paid_at ? " · " + formatDateDisplay(d.dupAlert.paid_at) : ""}`}>
                          ⚠️ Factura ya registrada
                        </div>
                      )}
                    </div>
                    {/* Concepto */}
                    <div className="flex-[2] min-w-[170px]">
                      <label className={fieldLabel}>Concepto</label>
                      <input type="text" className={cellInput} value={d.concept} placeholder="Concepto"
                        onChange={(e) => patchDraft(d.key, { concept: e.target.value, conceptGuess: false })} />
                      {d.conceptGuess && <div className="mt-0.5 text-[11px] text-violet-600">✨ sugerido · revisa</div>}
                    </div>
                    {/* Grupo */}
                    <div className="w-[150px]">
                      <label className={fieldLabel}>Grupo</label>
                      <select className={cellInput} value={d.groupId}
                        onChange={(e) => patchDraft(d.key, { groupId: e.target.value, groupGuess: false })}>
                        <option value="">— ninguno —</option>
                        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                      {d.groupGuess && (
                        <div className="mt-0.5 text-[11px] text-violet-600"
                          title={d.groupSource === "history" ? "Grupo usado antes con este proveedor." : "Grupo sugerido por la IA."}>
                          ✨ sugerido{d.groupSource === "history" ? " (histórico)" : ""}
                        </div>
                      )}
                    </div>
                    {/* Estado */}
                    <div className="w-[150px]">
                      <label className={fieldLabel}>Estado</label>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => patchDraft(d.key, { status: "pagada" })}
                          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${d.status === "pagada" ? "bg-[var(--primary)] text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>Pagada</button>
                        <button type="button" onClick={() => patchDraft(d.key, { status: "pendiente" })}
                          className={`flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${d.status === "pendiente" ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>Pend.</button>
                      </div>
                    </div>
                    {/* Importe */}
                    <div className="w-[110px]">
                      <label className={fieldLabel}>Importe</label>
                      <input type="number" step="0.01" min="0" className={cellInput} value={d.amount} placeholder="0.00"
                        onChange={(e) => patchDraft(d.key, { amount: e.target.value, amountTouched: true })}
                        onBlur={() => checkDraftPrice(d.key, d)} />
                      {d.toAlmacen && !d.amountTouched && d.amount !== "" && (
                        <div className="mt-0.5 text-[11px] leading-tight text-[var(--primary)]">✨ auto desde líneas</div>
                      )}
                      {d.alert && (
                        <div className="mt-0.5 text-[11px] leading-tight text-amber-700">
                          ⚠️ +{d.alert.pct_change}% vs últ.
                        </div>
                      )}
                    </div>
                    {/* IVA */}
                    <div className="w-[170px]">
                      <label className={fieldLabel}>IVA %</label>
                      <div className="flex items-center gap-1">
                        <input type="number" step="0.01" min="0" max="100" className={`${cellInput} w-14`} value={d.vatRate} placeholder="—"
                          onChange={(e) => patchDraft(d.key, { vatRate: e.target.value, vatGuess: false })} />
                        <button type="button" onClick={() => patchDraft(d.key, { vatRate: "21", vatGuess: false })}
                          className="rounded-md px-2 py-1.5 text-[11px] font-semibold text-[color:var(--wa-brand-foreground)] bg-[color:var(--wa-brand)] hover:bg-[color:var(--wa-brand-hover)]">21</button>
                        <button type="button" onClick={() => patchDraft(d.key, { vatRate: "10", vatGuess: false })}
                          className="rounded-md px-2 py-1.5 text-[11px] font-semibold text-[color:var(--wa-brand-foreground)] bg-[color:var(--wa-brand)] hover:bg-[color:var(--wa-brand-hover)]">10</button>
                      </div>
                      {cuota != null && <div className="mt-0.5 text-[11px] text-gray-400">cuota {cuota.toFixed(2)} €</div>}
                      {d.vatGuess && (
                        <div className="text-[11px] text-amber-600" title="Tipo no impreso; sugerido por la nueva ley (leche/café 10%, resto 21%).">
                          sugerido · revisa
                        </div>
                      )}
                    </div>
                    {/* Fecha */}
                    <div className="w-[170px]">
                      <label className={fieldLabel}>Fecha</label>
                      <div className="flex items-center gap-1">
                        <input type="date" className={cellInput} value={d.date}
                          onChange={(e) => patchDraft(d.key, { date: e.target.value })} />
                        <input type="time" className={`${cellInput} w-[72px]`} value={d.time}
                          onChange={(e) => patchDraft(d.key, { time: e.target.value })} />
                      </div>
                    </div>
                    {/* Acciones (a la derecha) */}
                    <div className="ml-auto flex flex-col items-end gap-1.5 pt-4">
                      <label className="flex items-center gap-1.5 text-[11px] font-medium text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" className="accent-[var(--primary)]" checked={d.toAlmacen}
                          onChange={(e) => patchDraft(d.key, { toAlmacen: e.target.checked })} />
                        ➕ Añadir a almacén
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px]">
                          {d.source === "ai" ? <span className="font-semibold text-violet-600" title="Extraído por IA">✨ IA</span>
                            : d.source === "rules" ? <span className="text-gray-400" title="Extraído por reglas">reglas</span>
                            : <span className="text-gray-300">manual</span>}
                        </span>
                        <button type="button" disabled={d.saving} onClick={() => registerDraft(d.key)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[var(--primary)] hover:bg-[var(--primary-strong)] text-white text-xs font-semibold transition-colors disabled:opacity-50">
                          <HiOutlineCheck className="w-4 h-4" /> Registrar
                        </button>
                        <button type="button" title="Descartar" onClick={() => removeDraft(d.key)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Sub-fila: entrada de mercancía — líneas de producto (solo si el tick está activo) */}
                  {d.toAlmacen && (
                    <div className="mt-2 rounded-lg border border-[var(--border)] bg-[var(--muted)]/40 p-2.5">
                      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--primary)]">Líneas de almacén</div>
                      <div className="space-y-2">
                        {d.lines.map((l) => (
                          <div key={l.key} className="flex flex-wrap items-end gap-x-3 gap-y-2">
                            <div className="flex-[2] min-w-[200px]">
                              <label className={fieldLabel}>Producto</label>
                              <input list={`prod-${d.key}-${l.key}`} className={cellInput} value={l.productName}
                                placeholder="Buscar producto…"
                                onChange={(e) => {
                                  const name = e.target.value;
                                  const match = products.find((p) => p.name === name);
                                  patchLine(d.key, l.key, { productName: name, productId: match ? match.id : "" });
                                }} />
                              <datalist id={`prod-${d.key}-${l.key}`}>
                                {products.map((p) => <option key={p.id} value={p.name} />)}
                              </datalist>
                              {l.productName && !l.productId && (
                                <div className="mt-0.5 text-[11px] text-amber-600">Elige un producto de la lista</div>
                              )}
                            </div>
                            <div className="w-[100px]">
                              <label className={fieldLabel}>Cantidad</label>
                              <input type="number" step="0.001" min="0" className={cellInput} value={l.qty} placeholder="0"
                                onChange={(e) => patchLine(d.key, l.key, { qty: e.target.value })} />
                            </div>
                            <div className="w-[130px]">
                              <label className={fieldLabel}>Coste unitario</label>
                              <input type="number" step="0.01" min="0" className={cellInput}
                                value={l.unitCostTouched ? l.unitCost : autoUnitCost(d, l)}
                                placeholder={autoUnitCost(d, l) ? "auto" : "—"}
                                onChange={(e) => patchLine(d.key, l.key, { unitCost: e.target.value, unitCostTouched: true })} />
                            </div>
                            <button type="button" title="Quitar línea" onClick={() => removeLine(d.key, l.key)}
                              disabled={d.lines.length <= 1}
                              className="mb-0.5 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 transition-colors">
                              <HiOutlineTrash className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => addLine(d.key)}
                        className="mt-2 text-[12px] font-semibold text-[var(--primary)] hover:text-[var(--primary)]">+ Añadir línea</button>
                      {d.lines.length === 1 && (
                        <span className="ml-2 text-[11px] text-gray-400">coste auto = base ÷ cantidad</span>
                      )}
                    </div>
                  )}

                  {d.error && <div className="mt-2 text-[12px] text-red-500">{d.error}</div>}
                </div>
              );
            })}
          </div>
        </>
        )}
      </div>

      {/* ── ZONA 2: Filtros ────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
        <div className="text-xs font-semibold uppercase tracking-widest text-gray-400">Filtrar / Buscar</div>
        {/* Buscador grande */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              className="w-full border border-gray-200 rounded-lg pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)] shadow-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") fetchPayments(); }}
              placeholder="Buscar por proveedor, CIF, nº de factura o concepto…"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600" title="Limpiar">
                <HiOutlineX className="w-4 h-4" />
              </button>
            )}
          </div>
          <button onClick={fetchPayments} className="px-5 py-2.5 bg-[var(--primary)] hover:bg-[var(--primary-strong)] text-white text-sm font-semibold rounded-lg transition-colors shadow-sm whitespace-nowrap">
            Buscar
          </button>
          <Button variant="primary" onClick={exportFilteredPayments} title="Descarga el Excel del corte filtrado">
            Exportar Excel
          </Button>
        </div>
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-500">Año</label>
            <select className={inputCls} value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-500">Grupo</label>
            <select className={`${inputCls} min-w-[160px]`} value={filterGroupId} onChange={(e) => setFilterGroupId(e.target.value)}>
              <option value="">Cualquier grupo</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-500">Estado</label>
            <select className={inputCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="pagada">Pagada</option>
            </select>
          </div>
          {(searchQuery || filterGroupId || statusFilter) && (
            <button type="button" onClick={() => { setSearchQuery(""); setFilterGroupId(""); setStatusFilter(""); }}
              className="text-sm text-gray-500 hover:text-gray-700 underline">
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* ── ZONA 3: Línea de tiempo + historial ────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="flex overflow-x-auto gap-1 p-3 bg-gray-50 border-b border-gray-200">
          {MONTHS.map((name, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedMonth(idx)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                selectedMonth === idx
                  ? "bg-[var(--primary)] text-white shadow-sm"
                  : "text-gray-500 hover:bg-gray-200"
              }`}
            >
              {name}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <TableHead>
              <Th>Proveedor</Th>
              <Th>Grupo</Th>
              <Th>Concepto</Th>
              <Th align="right">Importe</Th>
              <Th>Estado</Th>
              <Th>Fecha</Th>
              <Th align="right">Total: <span className="text-[var(--primary)]">{formatMoney(totalAmount)}</span>
                  {pendingAmount > 0 && <div className="text-[11px] font-medium text-amber-600 normal-case tracking-normal">Pendiente: {formatMoney(pendingAmount)}</div>}</Th>
            </TableHead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><Td align="center" colSpan={7} className="py-10 text-gray-400">Cargando…</Td></tr>
              ) : error ? (
                <tr><Td align="center" colSpan={7} className="py-10 text-red-500 text-sm">{error}</Td></tr>
              ) : payments.length === 0 ? (
                <tr><Td align="center" colSpan={7} className="py-10 text-gray-400">No hay pagos para este mes.</Td></tr>
              ) : (
                payments.map((p) => (
                  <Tr key={p.id}>
                    <Td className="py-2.5">
                      <div className="text-gray-800 font-medium">{p.supplier_name || <span className="text-gray-300">—</span>}</div>
                      {p.supplier_cif && <div className="text-[11px] text-gray-400">{p.supplier_cif}</div>}
                    </Td>
                    <Td className="py-2.5">
                      {p.group_name
                        ? <span className="inline-block bg-[var(--muted)] text-[var(--primary)] text-xs font-medium px-2 py-0.5 rounded-full">{p.group_name}</span>
                        : <span className="text-gray-300">—</span>}
                    </Td>
                    <Td className="py-2.5">
                      <div className="text-gray-800 font-medium">{p.concept}</div>
                      {p.invoice_number && <div className="text-[11px] text-gray-400">Nº {p.invoice_number}</div>}
                    </Td>
                    <Td align="right" className="py-2.5 font-semibold text-gray-900">{formatMoney(p.amount)}</Td>
                    <Td className="py-2.5">
                      {p.status === "pendiente"
                        ? <span className="inline-block bg-amber-100 text-amber-700 text-xs font-medium px-2 py-0.5 rounded-full">Pendiente</span>
                        : <span className="inline-block bg-[var(--muted)] text-[var(--primary)] text-xs font-medium px-2 py-0.5 rounded-full">Pagada</span>}
                    </Td>
                    <Td className="py-2.5 text-gray-400 text-xs">{formatDateDisplay(p.paid_at)}</Td>
                    <Td align="right" className="py-2.5">
                      <button onClick={() => handleDeletePayment(p.id)} title="Eliminar"
                        className="text-gray-300 hover:text-red-500 transition-colors">
                        <HiOutlineTrash className="w-4 h-4" />
                      </button>
                    </Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group manager modal */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              onClick={closeGroupModal}
            >
              <HiOutlineX className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Grupos</h2>
            <p className="text-sm text-gray-500 mb-4">Categorías para clasificar pagos (Rrhh, Dispensario, barra…)</p>

            {/* Lista de grupos: renombrar / borrar */}
            <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 border border-gray-100 rounded-lg mb-4">
              {groups.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Aún no hay grupos.</p>
              ) : (
                groups.map((g) => (
                  <div key={g.id} className="flex items-center gap-2 px-3 py-2">
                    {editGroupId === g.id ? (
                      <>
                        <input
                          type="text" autoFocus
                          className="flex-1 border border-gray-200 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
                          value={editGroupName}
                          onChange={(e) => setEditGroupName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") handleRenameGroup(g.id); if (e.key === "Escape") { setEditGroupId(null); setEditGroupName(""); } }}
                        />
                        <button type="button" title="Guardar" onClick={() => handleRenameGroup(g.id)}
                          className="p-1.5 rounded-md bg-[var(--primary)] hover:bg-[var(--primary-strong)] text-white transition-colors">
                          <HiOutlineCheck className="w-4 h-4" />
                        </button>
                        <button type="button" title="Cancelar" onClick={() => { setEditGroupId(null); setEditGroupName(""); }}
                          className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 transition-colors">
                          <HiOutlineX className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-sm text-gray-800">{g.name}</span>
                        <button type="button" title="Renombrar"
                          onClick={() => { setEditGroupId(g.id); setEditGroupName(g.name); setGroupError(""); }}
                          className="p-1.5 rounded-md text-gray-400 hover:text-[var(--primary)] hover:bg-[var(--muted)] transition-colors">
                          <HiOutlinePencil className="w-4 h-4" />
                        </button>
                        <button type="button" title="Eliminar" onClick={() => handleDeleteGroup(g.id)}
                          className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                          <HiOutlineTrash className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Crear nuevo grupo */}
            <form onSubmit={handleCreateGroup} className="space-y-3">
              <label className="text-xs font-semibold uppercase tracking-wide text-gray-400">Nuevo grupo</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ring)] shadow-sm"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Nombre del grupo"
                />
                <button type="submit" disabled={groupSubmitting || !newGroupName.trim()}
                  className="px-4 py-2 text-sm rounded-sm bg-[var(--primary)] hover:bg-[var(--primary-strong)] text-[var(--primary-foreground)] font-medium transition-colors disabled:opacity-50 whitespace-nowrap">
                  {groupSubmitting ? "Creando…" : "Crear"}
                </button>
              </div>
              {groupError && <p className="text-sm text-red-500">{groupError}</p>}
            </form>

            <div className="flex justify-end pt-4">
              <button type="button" onClick={closeGroupModal}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
      {dialogoConfirmar}
    </div>
  );
}
